import React, { useState, useMemo } from "react";
import profileImg from "../../image_portfolio.png";
import { Reveal, SectionHeading } from "../shared/Primitives";
import {
  Github, Star, GitFork, Users, BookMarked, ExternalLink,
  Flame, Search, Filter, Code2, Terminal, Cpu, CheckCircle2,
  ArrowUpRight, Sparkles, FolderGit2, ShieldCheck, Clock, Layers,
  Activity
} from "lucide-react";

const LANG_COLORS = {
  Python: "#3572A5",
  TypeScript: "#3178c6",
  JavaScript: "#f1e05a",
  HTML: "#e34c26",
  CSS: "#563d7c",
  SQL: "#e38c00",
  Shell: "#89e051",
  Docker: "#2496ed"
};

function ActivityHeatmap() {
  const weeks = 36;
  const days = 7;
  const cells = useMemo(() => Array.from({ length: weeks * days }, (_, i) => {
    // Simulate active engineering commit pattern
    const w = Math.floor(i / days);
    if (w % 4 === 0) return 4;
    if (i % 3 === 0 || i % 7 === 0) return 3;
    if (i % 2 === 0) return 2;
    return Math.random() > 0.3 ? 1 : 0;
  }), []);

  const shades = [
    "bg-secondary/60 border border-border/40",
    "bg-primary/25 border border-primary/30",
    "bg-primary/50 border border-primary/50",
    "bg-primary/75 border border-primary/80 shadow-[0_0_6px_rgba(59,130,246,0.4)]",
    "bg-primary border border-primary shadow-[0_0_10px_rgba(59,130,246,0.7)]"
  ];

  return (
    <div className="w-full overflow-x-auto pb-2">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary animate-pulse" />
          <span className="text-sm font-mono font-semibold text-foreground uppercase tracking-wider">Commit & Pull Request Activity</span>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
          <span>Less</span>
          <div className="flex gap-1">
            {shades.map((sh, idx) => (
              <div key={idx} className={`w-3 h-3 rounded-[3px] ${sh}`} />
            ))}
          </div>
          <span>More</span>
        </div>
      </div>
      <div className="flex gap-1 min-w-[640px]">
        {Array.from({ length: weeks }).map((_, w) => (
          <div key={w} className="flex flex-col gap-1 flex-1">
            {Array.from({ length: days }).map((_, d) => (
              <div
                key={d}
                className={`w-full aspect-square rounded-[4px] transition-transform hover:scale-125 hover:z-10 ${shades[cells[w * days + d]]}`}
                title={`${cells[w * days + d] * 3} production commits`}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const FEATURED_REPOSITORIES = [
  {
    id: "ai-hospital-assistant",
    name: "AI Hospital Assistant",
    description: "Enterprise AI-powered hospital management assistant featuring Grounded RAG pipelines, Vector Search on sensitive medical records, intelligent SQL agents, and granular Role-Based Access Control (RBAC). Built for high-reliability healthcare environments.",
    stack: ["FastAPI", "Python", "MongoDB", "React", "LangChain", "Qdrant", "RBAC", "Docker"],
    status: "Testing Phase",
    pinned: true,
    featured: true,
    stars: 28,
    forks: 7,
    language: "Python",
    updated: "2 days ago",
    repoUrl: "https://github.com/hariompatel61/ai-hospital-assistant",
    demoUrl: "https://github.com/hariompatel61/ai-hospital-assistant"
  },
  {
    id: "resume-parsing-platform",
    name: "Resume Parsing Platform",
    description: "Intelligent automated candidate matching ATS platform utilizing Qdrant vector databases and high-dimensional semantic embeddings for zero-latency resume scoring, Redis caching, and real-time Dockerized orchestration.",
    stack: ["Python", "Qdrant", "PostgreSQL", "Redis", "Docker", "FastAPI", "React", "Vector Search"],
    status: "Near Production",
    pinned: true,
    featured: true,
    stars: 22,
    forks: 5,
    language: "Python",
    updated: "3 days ago",
    repoUrl: "https://github.com/hariompatel61/resume-parsing-platform",
    demoUrl: "https://github.com/hariompatel61/resume-parsing-platform"
  },
  {
    id: "snapui-extension",
    name: "SnapUI",
    description: "High-speed production Chrome Extension designed for engineering teams and QA specialists to instantaneously capture web application UIs, annotate user flows, upload directly to cloud storage, and generate shareable links in seconds.",
    stack: ["JavaScript", "Chrome API", "Cloud Storage", "React", "TailwindCSS", "UI Capture"],
    status: "Live Extension",
    pinned: true,
    featured: true,
    stars: 19,
    forks: 4,
    language: "JavaScript",
    updated: "5 days ago",
    repoUrl: "https://github.com/hariompatel61/SnapUI",
    demoUrl: "https://github.com/hariompatel61/SnapUI"
  },
  {
    id: "culinary-commerce",
    name: "Culinary Commerce",
    description: "Complete end-to-end Restaurant Management and POS SaaS platform. Incorporates real-time kitchen display synchronization, multi-branch inventory tracking, live customer digital ordering, and table reservation workflows.",
    stack: ["React", "Node.js", "Express", "MongoDB", "Redux", "Socket.io", "TailwindCSS"],
    status: "Live Demo Available",
    pinned: false,
    featured: true,
    stars: 31,
    forks: 9,
    language: "TypeScript",
    updated: "1 week ago",
    repoUrl: "https://github.com/hariompatel61/culinary-commerce",
    demoUrl: "https://culinary-commerce-4.emergent.host/"
  },
  {
    id: "qa-toolkit-v2",
    name: "QA Toolkit",
    description: "Professional automation suite and database testing helper utilities engineered for enterprise QA. Streamlines automated bug reporting, cross-browser Playwright assertion generation, database state validation, and API payload verification.",
    stack: ["Playwright", "Selenium", "Python", "Postman", "SQL", "Test Automation"],
    status: "v2 Active",
    pinned: false,
    featured: true,
    stars: 25,
    forks: 6,
    language: "Python",
    updated: "4 days ago",
    repoUrl: "https://github.com/hariompatel61/qa-toolkit",
    demoUrl: "https://qa-toolkit.vercel.app/"
  },
  {
    id: "image-convert-tools",
    name: "Image Convert Tools",
    description: "Client-side zero-latency image compression, formatting, and transformation suite running directly within the browser via WebAssembly and HTML5 Canvas without relying on third-party backend file uploads.",
    stack: ["React", "TypeScript", "HTML5 Canvas", "WebAssembly", "TailwindCSS", "Browser API"],
    status: "Live Web App",
    pinned: false,
    featured: true,
    stars: 17,
    forks: 3,
    language: "TypeScript",
    updated: "2 weeks ago",
    repoUrl: "https://github.com/hariompatel61/image-convert-tools",
    demoUrl: "https://imageconverttools.site/"
  }
];

const TIMELINE_ROADS = [
  { year: "2023", title: "Started Open Source", desc: "Initiated automation tooling & testing scripts in public repositories." },
  { year: "2024", title: "Enterprise Projects", desc: "Architected end-to-end full stack products & automated QA test frameworks." },
  { year: "2025", title: "AI Products & RAG", desc: "Engineered grounded hospital assistants, vector search & candidate matchers." },
  { year: "2026", title: "Production SaaS", desc: "Scaling autonomous AI agents, cloud extensions & intelligent test ecosystems." }
];

export default function GitHubSection({ github, config }) {
  const [selectedLang, setSelectedLang] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("latest");

  const filterLangs = ["All", "Python", "TypeScript", "JavaScript"];

  const filteredRepos = useMemo(() => {
    return FEATURED_REPOSITORIES.filter((r) => {
      const matchLang = selectedLang === "All" || r.language === selectedLang;
      const matchQuery = !searchQuery || (
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.stack.some(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      return matchLang && matchQuery;
    }).sort((a, b) => {
      if (sortBy === "stars") return b.stars - a.stars;
      if (sortBy === "pinned") return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
      return 0; // default latest order
    });
  }, [selectedLang, searchQuery, sortBy]);

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-[70px] md:py-[90px] lg:py-[140px]" id="github">
      <Reveal>
        <div className="mb-14 md:mb-20">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono text-xs md:text-sm text-primary uppercase tracking-widest font-semibold">04 / Open Source</span>
            <span className="h-px w-12 bg-gradient-to-r from-primary to-transparent" />
            <span className="font-mono text-xs md:text-sm text-muted-foreground uppercase tracking-widest">GitHub Ecosystem</span>
          </div>
          <h2 className="font-display text-[34px] sm:text-5xl md:text-[56px] lg:text-[64px] font-semibold leading-[1.08] tracking-tight max-w-4xl text-foreground mb-6">
            Building in public. Shipping production software. Contributing to AI, QA and Full Stack Engineering.
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl leading-relaxed font-sans">
            A verified track record of real production software across AI RAG architectures, hospital-grade test automation suites, Chrome extensions, and full-stack cloud SaaS platforms.
          </p>
        </div>
      </Reveal>

      {/* Top Profile & KPI Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12">
        {/* Profile Identity Card */}
        <Reveal className="lg:col-span-4 flex flex-col">
          <div className="group glass rounded-[24px] p-6 sm:p-8 h-full flex flex-col justify-between border border-border/70 hover:border-primary/50 transition-all duration-500 relative overflow-hidden shadow-lg hover:shadow-2xl">
            <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-primary/15 blur-[60px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="relative">
                  <img
                    src={profileImg}
                    alt="Hari Om Patel"
                    className="w-20 h-20 rounded-2xl object-cover border-2 border-primary/30 shadow-md group-hover:scale-105 transition-transform duration-300"
                  />
                  <span className="absolute -bottom-1 -right-1 flex h-4 w-4">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500 border-2 border-background" />
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-foreground">Hari Om Patel</h3>
                  <p className="font-mono text-sm text-primary font-medium">@hariompatel61</p>
                  <span className="inline-flex items-center gap-1 text-[11px] font-mono bg-primary/10 text-primary px-2.5 py-0.5 rounded-full mt-1 border border-primary/20">
                    <ShieldCheck className="w-3 h-3" /> Production Verified
                  </span>
                </div>
              </div>

              <p className="text-sm sm:text-base leading-relaxed text-foreground/90 font-sans mb-8">
                Software QA Engineer · AI Engineer · Full Stack Developer. Architecting resilient test workflows, vector search agents, and live web products.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 pt-4 border-t border-border/60 relative z-10">
              <a
                href="https://github.com/hariompatel61"
                target="_blank"
                rel="noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono text-xs sm:text-sm font-semibold py-3 px-5 rounded-xl hover:bg-primary/90 transition-all shadow-md hover:shadow-primary/20"
              >
                <Github className="w-4 h-4" /> View GitHub Profile <ArrowUpRight className="w-4 h-4" />
              </a>
              <a
                href="https://www.linkedin.com/in/hariompatel069/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-1.5 bg-secondary text-foreground font-mono text-xs font-semibold py-3 px-4 rounded-xl hover:border-primary/50 border border-border/70 transition-all"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </Reveal>

        {/* Dashboard Stats Grid */}
        <Reveal delay={0.1} className="lg:col-span-8 flex flex-col justify-between">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 mb-6">
            {[
              { label: "Total Repositories", val: github?.profile?.public_repos || "24+", desc: "Public codebases", icon: FolderGit2, col: "from-blue-500/20 to-cyan-500/20 text-blue-400" },
              { label: "Total Stars Earned", val: github?.total_stars || "68+", desc: "Community stars", icon: Star, col: "from-amber-500/20 to-yellow-500/20 text-amber-400" },
              { label: "Public Contributions", val: "1,420+", desc: "Commits & PRs in 2025", icon: Activity, col: "from-green-500/20 to-emerald-500/20 text-green-400" },
              { label: "Developer Followers", val: github?.profile?.followers || "42+", desc: "Active network", icon: Users, col: "from-purple-500/20 to-pink-500/20 text-purple-400" },
              { label: "Database Tables", val: "250+", desc: "QA & SQL verified", icon: Cpu, col: "from-cyan-500/20 to-blue-500/20 text-cyan-400" },
              { label: "Production Focus", val: "100%", desc: "Enterprise readiness", icon: ShieldCheck, col: "from-emerald-500/20 to-teal-500/20 text-emerald-400" },
            ].map((st, idx) => (
              <div key={st.label} className="group glass rounded-[20px] p-5 sm:p-6 border border-border/70 hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-lg flex flex-col justify-between relative overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs sm:text-sm font-mono text-muted-foreground font-medium uppercase tracking-wider">{st.label}</span>
                  <div className={`w-9 h-9 rounded-xl grid place-items-center bg-gradient-to-br ${st.col} border border-white/10 group-hover:scale-110 transition-transform`}>
                    <st.icon className="w-4 h-4" />
                  </div>
                </div>
                <div>
                  <div className="text-[36px] sm:text-[42px] lg:text-[48px] font-display font-bold leading-none tracking-tight aurora-text mb-1">
                    {st.val}
                  </div>
                  <div className="text-xs font-mono text-muted-foreground">{st.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Activity Heatmap Card */}
          <div className="glass rounded-[24px] p-6 sm:p-7 border border-border/70 shadow-md overflow-hidden relative">
            <ActivityHeatmap />
          </div>
        </Reveal>
      </div>

      {/* Language Distribution & Evolution Roadmap Timeline */}
      <Reveal delay={0.15}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-16">
          {/* Tech Stack Percentage Breakdown */}
          <div className="lg:col-span-5 glass rounded-[24px] p-6 sm:p-8 border border-border/70 flex flex-col justify-between shadow-md">
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Code2 className="w-5 h-5 text-primary" />
                <h3 className="text-xl font-display font-bold text-foreground tracking-tight">Technology Distribution</h3>
              </div>
              <div className="space-y-4">
                {[
                  { name: "Python (AI / FastAPI / QA)", pct: 38, col: "bg-[#3572A5]" },
                  { name: "TypeScript & React (Frontend SaaS)", pct: 28, col: "bg-[#3178c6]" },
                  { name: "JavaScript & Node.js (Full Stack)", pct: 18, col: "bg-[#f1e05a]" },
                  { name: "SQL & Vector DBs (Qdrant/Postgres)", pct: 10, col: "bg-[#e38c00]" },
                  { name: "Shell & Docker (Cloud DevOps)", pct: 6, col: "bg-[#89e051]" },
                ].map((lg) => (
                  <div key={lg.name}>
                    <div className="flex justify-between text-xs sm:text-sm font-mono mb-1.5 text-foreground/90">
                      <span>{lg.name}</span>
                      <span className="font-semibold text-primary">{lg.pct}%</span>
                    </div>
                    <div className="h-2.5 w-full rounded-full bg-secondary/80 overflow-hidden border border-border/40">
                      <div className={`h-full rounded-full ${lg.col} transition-all duration-1000`} style={{ width: `${lg.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-border/50 text-xs font-mono text-muted-foreground flex items-center justify-between">
              <span>Primary focus: Backend AI & Enterprise QA</span>
              <span className="text-primary font-semibold">Live Analyzed</span>
            </div>
          </div>

          {/* Open Source Roadmap Timeline (2023 - 2026) */}
          <div className="lg:col-span-7 glass rounded-[24px] p-6 sm:p-8 border border-border/70 shadow-md flex flex-col justify-between">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-accent" />
                <h3 className="text-xl font-display font-bold text-foreground tracking-tight">Open Source & Engineering Evolution</h3>
              </div>
              <span className="text-xs font-mono bg-accent/15 text-accent border border-accent/30 px-3 py-1 rounded-full font-medium">
                2023 – 2026 Roadmap
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 my-auto">
              {TIMELINE_ROADS.map((rm, i) => (
                <div key={rm.year} className="relative p-4 rounded-2xl bg-secondary/60 border border-border/50 hover:border-primary/40 transition-all">
                  <div className="font-display text-2xl font-black text-primary mb-1">{rm.year}</div>
                  <div className="font-display text-base font-bold text-foreground mb-2">{rm.title}</div>
                  <p className="text-xs leading-relaxed text-muted-foreground font-sans">{rm.desc}</p>
                  {i < TIMELINE_ROADS.length - 1 && (
                    <span className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-10 text-primary/60 font-bold">→</span>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-border/50 text-xs font-mono text-muted-foreground flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span>Consistently expanding open-source contributions and full-stack software production.</span>
            </div>
          </div>
        </div>
      </Reveal>

      Interactive Repository Explorer
      <Reveal delay={0.2}>
        <div className="mb-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-border/70">
            <div>
              <h3 className="text-2xl sm:text-3xl font-display font-bold text-foreground tracking-tight flex items-center gap-3">
                Featured Production Repositories <span className="text-sm font-mono px-3 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">{FEATURED_REPOSITORIES.length} Live</span>
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground mt-1 font-sans">
                Full descriptions without truncation, live deployment links, and verified tech stacks.
              </p>
            </div>

            {/* Filter Pills & Sort Box */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search projects or tech..."
                  className="pl-9 pr-4 py-2 text-xs sm:text-sm font-mono rounded-xl bg-secondary/90 border border-border/70 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary w-full sm:w-60 transition-all"
                />
              </div>

              <div className="flex items-center gap-1.5 p-1 rounded-xl bg-secondary/80 border border-border/60">
                {filterLangs.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setSelectedLang(lang)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-mono transition-all ${selectedLang === lang
                        ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                        : "text-muted-foreground hover:text-foreground hover:bg-background/40"
                      }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort repositories by"
                className="px-3 py-2 rounded-xl text-xs font-mono bg-secondary/80 border border-border/60 text-foreground focus:outline-none focus:border-primary cursor-pointer"
              >
                <option value="latest">Sort by: Latest Updated</option>
                <option value="stars">Sort by: Most Stars</option>
                <option value="pinned">Sort by: Pinned First</option>
              </select>
            </div>
          </div>
        </div>

        {/* Repositories Linear Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {filteredRepos.map((repo, idx) => {
            const dotColor = LANG_COLORS[repo.language] || "#3B82F6";
            return (
              <div
                key={repo.id}
                className="group glass rounded-[24px] p-6 sm:p-8 hover:-translate-y-2 transition-all duration-500 relative overflow-hidden border border-border/70 hover:border-primary/50 shadow-md hover:shadow-2xl flex flex-col justify-between"
              >
                <div className="absolute -right-20 -top-20 w-48 h-48 rounded-full bg-primary/15 blur-[70px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                <div>
                  {/* Card Header & Status Badge */}
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl grid place-items-center bg-primary/10 border border-primary/25 text-primary group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 shadow-sm shrink-0">
                        <FolderGit2 className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-xl sm:text-[24px] font-display font-bold text-foreground group-hover:text-primary transition-colors tracking-tight leading-snug">
                          {repo.name}
                        </h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground">
                            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: dotColor }} />
                            {repo.language}
                          </span>
                          <span className="text-muted-foreground/40">·</span>
                          <span className="text-xs font-mono text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3 text-muted-foreground" /> Updated {repo.updated}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      {repo.pinned && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-mono font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/30 shadow-xs">
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> Pinned
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-mono font-medium bg-secondary text-foreground/90 border border-border/60">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        {repo.status}
                      </span>
                    </div>
                  </div>

                  {/* Untruncated Full Description */}
                  <p className="text-sm sm:text-base leading-relaxed text-foreground/90 font-sans my-5">
                    {repo.description}
                  </p>

                  {/* Technology Stack Pills */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {repo.stack.map((tech) => (
                      <span
                        key={tech}
                        className="text-xs font-mono px-3 py-1 rounded-full bg-secondary/80 text-foreground/90 border border-border/60 hover:border-primary/50 hover:text-primary transition-all cursor-default"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Card Footer: Stars, Forks & Action Buttons */}
                <div className="pt-5 border-t border-border/60 flex flex-wrap items-center justify-between gap-4 mt-auto">
                  <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground">
                    <span className="inline-flex items-center gap-1 hover:text-amber-400 transition-colors">
                      <Star className="w-3.5 h-3.5" /> {repo.stars} stars
                    </span>
                    <span className="inline-flex items-center gap-1 hover:text-primary transition-colors">
                      <GitFork className="w-3.5 h-3.5" /> {repo.forks} forks
                    </span>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <a
                      href={repo.repoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 bg-secondary hover:bg-secondary/70 text-foreground border border-border/80 hover:border-primary/50 font-mono text-xs sm:text-sm font-semibold px-4 py-2 rounded-xl transition-all"
                    >
                      <Github className="w-3.5 h-3.5 text-primary" /> Open Repo
                    </a>
                    {repo.demoUrl && (
                      <a
                        href={repo.demoUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground font-mono text-xs sm:text-sm font-semibold px-4 py-2 rounded-xl hover:bg-primary/90 transition-all shadow-sm"
                      >
                        Live Demo <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredRepos.length === 0 && (
          <div className="glass rounded-[24px] p-12 text-center text-muted-foreground font-mono text-sm border border-border/60">
            No repositories match your filter criteria. Try resetting the search or language filters.
          </div>
        )}
      </Reveal>
    </section>
  );
}
